// src/components/solutions/SolutionsHero.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@dopomogai/ui';
import { TickerCarousel } from '@dopomogai/ui';

interface HeroData {
  headline?: string;
  verbs: string[];
  nouns: string[];
  solutionTypes: string[];
  staticText: string;
  moto: string;
  subheadline: string;
  cta: string;
}

interface SolutionsHeroProps {
  heroData: HeroData;
  onAction: (actionType: string, payload?: any) => void;
}

export const SolutionsHero: React.FC<SolutionsHeroProps> = ({ heroData, onAction }) => {
  return (
    <section className="bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(#2e3c51_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_60%,transparent_100%)]"></div>
      
      {/* Increased vertical padding for better mobile spacing */}
      <div className="container mx-auto px-4 min-h-[70vh] flex items-start justify-center relative z-10 pt-24 pb-16 md:pt-16 md:pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="flex flex-col items-center justify-center text-center max-w-7xl"
        >
          {/* Headline as primary heading */}
          {heroData.headline && (
            // FIX: Smaller base font size for mobile, scaling up on larger screens.
            <h1 className="font-extrabold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-tight drop-shadow-lg text-white mb-6 md:mb-8">
              {heroData.headline}
            </h1>
          )}

          {/* Row 1: Verb + Noun carousels */}
          {/* FIX: 
              - Removed h-28 to allow natural height.
              - Changed to flex-col by default, switching to md:flex-row on medium screens.
              - Adjusted font sizes to be smaller on mobile.
              - Adjusted gap to work for both layouts.
          */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-y-1 md:gap-x-6 mb-1 md:mb-2 text-3xl md:text-4xl lg:text-5xl font-extrabold">
            <TickerCarousel
              items={heroData.verbs}
              // FIX: Reduced height to match the smaller mobile font size
              itemHeightRem={5.5} 
              intervalSeconds={2.0}
              activeColor="text-emerald-400"
              inactiveColor="text-slate-500"
            />
            <TickerCarousel
              items={heroData.nouns}
              // FIX: Reduced height to match the smaller mobile font size
              itemHeightRem={5.5}
              intervalSeconds={2.2}
              activeColor="text-emerald-400"
              inactiveColor="text-slate-500"
            />
          </div>

          {/* Row 2: Static Text + Solution Type carousel */}
          {/* FIX: 
              - Removed h-28 and negative margin on mobile.
              - Changed to flex-col by default, switching to md:flex-row.
              - Adjusted font sizes to be smaller on mobile.
              - Adjusted gap for both layouts.
          */}
          <div className="md:-mt-2 flex flex-col md:flex-row flex-wrap justify-center items-center gap-y-1 md:gap-x-6 text-3xl md:text-4xl lg:text-5xl font-extrabold">
            <span className="text-white font-semibold">{heroData.staticText}</span>
            <TickerCarousel
              items={heroData.solutionTypes}
              // FIX: Reduced height to match the smaller mobile font size
              itemHeightRem={5.5}
              intervalSeconds={2.4}
              activeColor="text-emerald-400"
              inactiveColor="text-slate-500"
            />
          </div>
          
          <h2 className="mt-8 text-xl md:text-3xl font-bold text-white">
            {heroData.moto}
          </h2>

          <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg text-slate-300">
            {heroData.subheadline}
          </p>
          
          <div className="mt-10">
            <Button
              text={heroData.cta ?? 'Get AI Build Blueprint for Free'}
              variant="primary"
              size="lg"
              onClick={() => onAction('NAVIGATE_TO_PAGE', { path: '/contact?subject=AIAudit' })}
              className="hover:shadow-[4px_4px_0_0_#34d399]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};